import { useState } from "react";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel,
    AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * BrutalConfirm — neo-brutalist replacement for native `window.confirm`.
 *
 * Returns an imperative `confirm(opts) => Promise<boolean>` API mirroring
 * the browser primitive, plus a `<BrutalConfirmHost />` element that the
 * page must render once. Designed for piecewise migration from
 * `window.confirm` without rewriting every caller's structure.
 *
 * Usage:
 *   const { confirm, hostElement } = useBrutalConfirm();
 *   ...
 *   const ok = await confirm({
 *     title: "Reopen tournament?",
 *     body: "League admin will be able to edit again.",
 *     confirmLabel: "Reopen",
 *   });
 *   if (!ok) return;
 *   ...
 *   {hostElement}
 */
export function useBrutalConfirm() {
    const [state, setState] = useState({
        open: false, title: "", body: "", confirmLabel: "Confirm",
        cancelLabel: "Cancel", destructive: false, resolve: null,
    });

    const confirm = (opts = {}) => new Promise((resolve) => {
        setState({
            open: true,
            title: opts.title || "Are you sure?",
            body: opts.body || "",
            confirmLabel: opts.confirmLabel || "Confirm",
            cancelLabel: opts.cancelLabel || "Cancel",
            destructive: !!opts.destructive,
            resolve,
        });
    });

    const handle = (answer) => {
        const r = state.resolve;
        setState((s) => ({ ...s, open: false, resolve: null }));
        if (r) r(answer);
    };

    const hostElement = (
        <AlertDialog open={state.open} onOpenChange={(o) => { if (!o) handle(false); }}>
            <AlertDialogContent data-testid="brutal-confirm-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle data-testid="brutal-confirm-title">
                        {state.title}
                    </AlertDialogTitle>
                    {state.body && (
                        <AlertDialogDescription
                            data-testid="brutal-confirm-body"
                            className="whitespace-pre-wrap"
                        >
                            {state.body}
                        </AlertDialogDescription>
                    )}
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="brutal-confirm-cancel" onClick={() => handle(false)}>
                        {state.cancelLabel}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        data-testid="brutal-confirm-ok"
                        onClick={() => handle(true)}
                        className={state.destructive
                            ? "bg-blaze text-black border-black hover:bg-blaze/90"
                            : "bg-cyber text-black border-black hover:bg-cyber/90"}
                    >
                        {state.confirmLabel}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );

    return { confirm, hostElement };
}
